import { Slot } from '@radix-ui/react-slot'
import { cva, type VariantProps } from 'class-variance-authority'
import * as React from 'react'

import { cn } from '#app/utils/misc.tsx'
import { Spinner } from './spinner.tsx'

const buttonVariants = cva(
	'ring-offset-background focus-visible:ring-ring inline-flex cursor-pointer items-center justify-center whitespace-nowrap outline-hidden transition-all focus-visible:ring-2 focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50',
	{
		variants: {
			variant: {
				primary: '',
				secondary: '',
				success: '',
				warning: '',
				error: '',
				info: '',
			},
			appearance: {
				solid: '',
				ghost: '',
				text: '',
				outline: '',
				stroke: '',
			},
			size: {
				xs: 'font-parkinsans h-[14px] min-w-[30px] gap-1 px-2 text-xs font-medium', // 50px min width
				s: 'font-parkinsans h-[27px] min-w-[60px] gap-2 px-2 text-xs font-medium', // 60px min width
				m: 'font-parkinsans h-[40px] min-w-[80px] gap-2 px-4 text-sm font-medium', // 80px min width
				l: 'font-parkinsans h-[52px] min-w-[100px] gap-2 px-6 text-base font-medium', // 100px min width
			},
			isLoading: {
				true: 'cursor-wait',
				false: '',
			},
			iconOnly: {
				true: 'min-w-0 items-center justify-center',
				false: '',
			},
		},
		compoundVariants: [
			// PRIMARY (Blue) - Main action color - Figma Design System
			{
				variant: 'primary',
				appearance: 'solid',
				className:
					'shadow-2 bg-blue-500 text-white hover:bg-blue-600 active:bg-blue-700',
			},
			{
				variant: 'primary',
				appearance: 'ghost',
				className:
					'bg-transparent text-blue-600 hover:bg-blue-50 active:bg-blue-100',
			},
			{
				variant: 'primary',
				appearance: 'text',
				className:
					'bg-transparent text-blue-600 hover:text-blue-700 active:text-blue-800',
			},
			{
				variant: 'primary',
				appearance: 'outline',
				className:
					'border border-blue-300 bg-transparent text-blue-600 hover:bg-blue-50 active:bg-blue-100',
			},

			// SECONDARY (Green) - Secondary actions - Figma Design System
			{
				variant: 'secondary',
				appearance: 'solid',
				className:
					'shadow-2 bg-green-500 text-white hover:bg-green-600 active:bg-green-700',
			},
			{
				variant: 'secondary',
				appearance: 'ghost',
				className:
					'bg-transparent text-green-600 hover:bg-green-50 active:bg-green-100',
			},
			{
				variant: 'secondary',
				appearance: 'text',
				className:
					'bg-transparent text-green-600 hover:text-green-700 active:text-green-800',
			},
			{
				variant: 'secondary',
				appearance: 'outline',
				className:
					'border border-green-300 bg-transparent text-green-600 hover:bg-green-50 active:bg-green-100',
			},

			// SUCCESS (Green semantic) - Success actions
			{
				variant: 'success',
				appearance: 'solid',
				className:
					'bg-success-600 hover:bg-success-700 active:bg-success-800 text-white shadow-sm',
			},
			{
				variant: 'success',
				appearance: 'ghost',
				className:
					'text-success-700 hover:bg-success-50 active:bg-success-100 bg-transparent',
			},
			{
				variant: 'success',
				appearance: 'text',
				className:
					'text-success-700 hover:text-success-800 active:text-success-900 bg-transparent',
			},
			{
				variant: 'success',
				appearance: 'outline',
				className:
					'text-success-700 border-success-300 hover:bg-success-50 active:bg-success-100 border bg-transparent',
			},

			// WARNING (Yellow/Orange) - Warning actions
			{
				variant: 'warning',
				appearance: 'solid',
				className:
					'bg-warning-600 hover:bg-warning-700 active:bg-warning-800 text-white shadow-sm',
			},
			{
				variant: 'warning',
				appearance: 'ghost',
				className:
					'text-warning-700 hover:bg-warning-50 active:bg-warning-100 bg-transparent',
			},
			{
				variant: 'warning',
				appearance: 'text',
				className:
					'text-warning-700 hover:text-warning-800 active:text-warning-900 bg-transparent',
			},
			{
				variant: 'warning',
				appearance: 'outline',
				className:
					'text-warning-700 border-warning-300 hover:bg-warning-50 active:bg-warning-100 border bg-transparent',
			},

			// ERROR (Red) - Destructive actions
			{
				variant: 'error',
				appearance: 'solid',
				className:
					'bg-error-600 hover:bg-error-700 active:bg-error-800 text-white shadow-sm',
			},
			{
				variant: 'error',
				appearance: 'ghost',
				className:
					'text-error-700 hover:bg-error-50 active:bg-error-100 bg-transparent',
			},
			{
				variant: 'error',
				appearance: 'text',
				className:
					'text-error-700 hover:text-error-800 active:text-error-900 bg-transparent',
			},
			{
				variant: 'error',
				appearance: 'outline',
				className:
					'text-error-700 border-error-300 hover:bg-error-50 active:bg-error-100 border bg-transparent',
			},

			// INFO (Blue) - Informational actions
			{
				variant: 'info',
				appearance: 'solid',
				className:
					'bg-info-600 hover:bg-info-700 active:bg-info-800 text-white shadow-sm',
			},
			{
				variant: 'info',
				appearance: 'ghost',
				className:
					'text-info-700 hover:bg-info-50 active:bg-info-100 bg-transparent',
			},
			{
				variant: 'info',
				appearance: 'text',
				className:
					'text-info-700 hover:text-info-800 active:text-info-900 bg-transparent',
			},
			{
				variant: 'info',
				appearance: 'outline',
				className:
					'text-info-700 border-info-300 hover:bg-info-50 active:bg-info-100 border bg-transparent',
			},

			// STROKE (Custom stroke appearance) - Using design system token colors
			{
				variant: 'primary',
				appearance: 'stroke',
				className:
					'border-[1.25px] border-blue-500 bg-transparent text-blue-600 hover:bg-blue-50 active:border-blue-600 active:bg-blue-100 disabled:border-gray-300 disabled:bg-transparent disabled:text-gray-400',
			},
			{
				variant: 'secondary',
				appearance: 'stroke',
				className:
					'border-[1.25px] border-green-500 bg-transparent text-green-600 hover:bg-green-50 active:border-green-600 active:bg-green-100 disabled:border-gray-300 disabled:bg-transparent disabled:text-gray-400',
			},
			{
				variant: 'success',
				appearance: 'stroke',
				className:
					'border-success-500 text-success-600 hover:bg-success-50 active:bg-success-100 active:border-success-600 border-[1.25px] bg-transparent disabled:border-gray-300 disabled:bg-transparent disabled:text-gray-400',
			},
			{
				variant: 'warning',
				appearance: 'stroke',
				className:
					'border-warning-500 text-warning-600 hover:bg-warning-50 active:bg-warning-100 active:border-warning-600 border-[1.25px] bg-transparent disabled:border-gray-300 disabled:bg-transparent disabled:text-gray-400',
			},
			{
				variant: 'error',
				appearance: 'stroke',
				className:
					'border-error-500 text-error-600 hover:bg-error-50 active:bg-error-100 active:border-error-600 border-[1.25px] bg-transparent disabled:border-gray-300 disabled:bg-transparent disabled:text-gray-400',
			},
			{
				variant: 'info',
				appearance: 'stroke',
				className:
					'border-info-500 text-info-600 hover:bg-info-50 active:bg-info-100 active:border-info-600 border-[1.25px] bg-transparent disabled:border-gray-300 disabled:bg-transparent disabled:text-gray-400',
			},

			// Border radius adjustments per Figma specs (must come before iconOnly)
			{
				size: 'xs',
				className: 'rounded-2xl', // Figma Extra Small: 16px radius
			},
			{
				size: 's',
				className: 'rounded-3xl', // Figma Small: 24px radius
			},
			{
				size: 'm',
				className: 'rounded-xl', // Figma Medium: 12px radius
			},
			{
				size: 'l',
				className: 'rounded-xl', // Figma Large: 12px radius
			},

			// Icon only adjustments - MUST BE LAST for proper override
			{
				iconOnly: true,
				size: 'xs',
				className:
					'!flex !aspect-square !h-[20px] !w-[20px] !max-w-[20px] !min-w-[20px] !items-center !justify-center !rounded-full !p-[4px]', // 24px círculo perfecto
			},
			{
				iconOnly: true,
				size: 's',
				className:
					'!flex !aspect-square !h-[27px] !w-[27px] !max-w-[27px] !min-w-[27px] !items-center !justify-center !rounded-full !p-[5.5px]', // 27px círculo perfecto
			},
			{
				iconOnly: true,
				size: 'm',
				className:
					'!flex !aspect-square !h-[40px] !w-[40px] !max-w-[40px] !min-w-[40px] !items-center !justify-center !rounded-full !p-[10px]', // 40px círculo perfecto
			},
			{
				iconOnly: true,
				size: 'l',
				className:
					'!flex !aspect-square !h-[52px] !w-[52px] !max-w-[52px] !min-w-[52px] !items-center !justify-center !rounded-full !p-[14px]', // 52px círculo perfecto
			},
		],
		defaultVariants: {
			variant: 'primary',
			appearance: 'solid',
			size: 'm',
			isLoading: false,
			iconOnly: false,
		},
	},
)

export interface ButtonProps
	extends React.ComponentProps<'button'>,
		VariantProps<typeof buttonVariants> {
	/**
	 * Button semantic variant - maps to design system colors
	 */
	variant?: 'primary' | 'secondary' | 'success' | 'warning' | 'error' | 'info'
	/**
	 * Button appearance style
	 */
	appearance?: 'solid' | 'ghost' | 'text' | 'outline' | 'stroke'
	/**
	 * Button size
	 */
	size?: 'xs' | 's' | 'm' | 'l'
	/**
	 * If set to `true`, the button will be rendered as a child within the component.
	 * This child component must be a valid React component.
	 */
	asChild?: boolean
	/**
	 * Shows loading state with spinner
	 */
	isLoading?: boolean
	/**
	 * Icon to display in the button (usually on the left)
	 */
	leftIcon?: React.ReactNode
	/**
	 * Icon to display on the right side of the button
	 */
	rightIcon?: React.ReactNode
	/**
	 * If set to `true`, the button will be rendered as an icon-only button
	 */
	iconOnly?: boolean
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
	(
		{
			className,
			variant = 'primary',
			appearance = 'solid',
			size = 'm',
			asChild = false,
			isLoading = false,
			leftIcon,
			rightIcon,
			iconOnly = false,
			children,
			disabled,
			...props
		},
		ref,
	) => {
		const isDisabled = disabled || isLoading
		const hasOnlyIcon = iconOnly || (!children && (!!leftIcon || !!rightIcon))

		const Comp = asChild ? Slot : 'button'

		// When using asChild, we need to handle children differently
		if (asChild) {
			// With asChild, we can't add loading states, icons, etc.
			// The child element takes full control
			return (
				<Comp
					ref={ref}
					data-slot="button"
					className={cn(
						buttonVariants({
							variant,
							appearance,
							size,
							isLoading: false, // No loading state with asChild
							iconOnly: hasOnlyIcon,
						}),
						className,
					)}
					disabled={isDisabled}
					{...props}
				>
					{children}
				</Comp>
			)
		}

		// Icon-only button behavior
		if (hasOnlyIcon) {
			return (
				<Comp
					ref={ref}
					data-slot="button"
					className={cn(
						buttonVariants({
							variant,
							appearance,
							size,
							isLoading,
							iconOnly: hasOnlyIcon,
						}),
						className,
					)}
					disabled={isDisabled}
					{...props}
				>
					{isLoading && (
						<Spinner
							size={size === 's' ? 'small' : size === 'm' ? 'medium' : 'large'}
							// variant="white"
							className="shrink-0"
						/>
					)}
					{!isLoading &&
						leftIcon &&
						(React.isValidElement(leftIcon)
							? React.cloneElement(leftIcon, {
									size: size === 's' ? 'sm' : size === 'm' ? 'md' : 'lg',
								} as any)
							: leftIcon)}
					{!isLoading &&
						rightIcon &&
						(React.isValidElement(rightIcon)
							? React.cloneElement(rightIcon, {
									size: size === 's' ? 'sm' : size === 'm' ? 'md' : 'lg',
								} as any)
							: rightIcon)}
					{!isLoading ? children : undefined}
				</Comp>
			)
		}

		// Normal button behavior
		return (
			<Comp
				ref={ref}
				data-slot="button"
				className={cn(
					buttonVariants({
						variant,
						appearance,
						size,
						isLoading,
						iconOnly: hasOnlyIcon,
					}),
					className,
				)}
				disabled={isDisabled}
				{...props}
			>
				{isLoading && (
					<Spinner
						size={size === 's' ? 'small' : size === 'm' ? 'medium' : 'large'}
						variant="white"
						// className="mr-2"
					/>
				)}
				{!isLoading && leftIcon && (
					<span className="flex h-4 w-4 shrink-0 items-center justify-center">
						{leftIcon}
					</span>
				)}
				{children}
				{!isLoading && rightIcon && (
					<span className="flex h-3.5 w-3.5 shrink-0 items-center justify-center">
						{rightIcon}
					</span>
				)}
			</Comp>
		)
	},
)

Button.displayName = 'Button'

export { Button, buttonVariants }
